import { Box, Heading, Icon, Paragraph, Pill, PillKind, Spinner, Stack, Text } from '@sixfold/common-ui';
import classnames from 'classnames';
import React from 'react';
import { Link, useLocation } from 'react-router';

import { getStatusLabelColor } from '../../tour/utils';
import { CheckStatus, Health, HEALTH_KIND, HEALTH_LABEL } from '../diagnostics';

import styles from './mobile.module.css';

export const Card: React.FC<{
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, actions, children }) => (
  <Box className={styles.card} padding="large" elevation="raised" radius="small">
    <Stack className={styles.cardHeader} justifyContent="space-between" alignItems="start" gap="small" wrap={false}>
      <Stack direction="column" wrap={false} gap="xsmall">
        <Heading type="title" size="small">
          {title}
        </Heading>
        {subtitle !== undefined && (
          <Text size="small" prominence="subtle">
            {subtitle}
          </Text>
        )}
      </Stack>
      {actions}
    </Stack>
    {children}
  </Box>
);

type StatTone = 'default' | 'danger' | 'warning' | 'success';

const TONE_CLASS: Record<StatTone, string | undefined> = {
  default: undefined,
  danger: styles.statValueDanger,
  warning: styles.statValueWarning,
  success: styles.statValueSuccess,
};

const viewSearch = (search: string) => {
  const params = new URLSearchParams(search);
  params.delete('page');
  params.sort();
  return params.toString();
};

export const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: StatTone;
  loading?: boolean;
  pathname: string;
  search?: string;
}> = ({ label, value, hint, tone = 'default', loading = false, pathname, search = '' }) => {
  const location = useLocation();
  const active = location.pathname === pathname && viewSearch(location.search) === viewSearch(search);
  return (
    <Box
      as={Link}
      to={{ pathname, search: active ? '' : search }}
      aria-current={active ? 'page' : undefined}
      className={classnames(styles.statCard, styles.statCardLink, active && styles.statCardActive)}
      padding="medium"
      elevation="raised"
      radius="small">
      <Stack direction="column" wrap={false} gap="xsmall">
        <Text size="small" prominence="subtle">
          {label}
        </Text>
        <span className={classnames(styles.statValue, !loading && TONE_CLASS[tone])}>
          {loading ? <Spinner size="small" /> : value}
        </span>
        {!loading && hint !== undefined && (
          <Text size="small" prominence="subtle">
            {hint}
          </Text>
        )}
      </Stack>
    </Box>
  );
};

export const HealthPill: React.FC<{ health: Health }> = ({ health }) => (
  <Pill kind={HEALTH_KIND[health]}>{HEALTH_LABEL[health]}</Pill>
);

const STATUS_KIND: Record<string, PillKind> = {
  green: 'success',
  red: 'danger',
  yellow: 'warning',
  teal: 'info',
  grey: 'neutral',
};

export const TourStatusPill: React.FC<{ status: string | null }> = ({ status }) => (
  <Pill kind={STATUS_KIND[getStatusLabelColor(status ?? '')] ?? 'neutral'}>{status ?? 'unknown'}</Pill>
);

const CHECK_ICON: Record<
  CheckStatus,
  {
    name:
      | 'small-circular-check'
      | 'small-circular-cross'
      | 'small-triangular-alert'
      | 'small-circular-clock'
      | 'small-info';
    className: string;
    label: string;
  }
> = {
  ok: { name: 'small-circular-check', className: styles.checkOk, label: 'Passed' },
  failed: { name: 'small-circular-cross', className: styles.checkFailed, label: 'Failed' },
  warning: { name: 'small-triangular-alert', className: styles.checkWarning, label: 'Warning' },
  pending: { name: 'small-circular-clock', className: styles.checkPending, label: 'Pending' },
  unknown: { name: 'small-info', className: styles.checkUnknown, label: 'No data' },
};

export const CheckIcon: React.FC<{ status: CheckStatus }> = ({ status }) => {
  const icon = CHECK_ICON[status];
  return (
    <span className={classnames(styles.checkIcon, icon.className)} aria-label={icon.label}>
      <Icon name={icon.name} size={16} />
    </span>
  );
};

export const EmptyState: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <Stack className={styles.emptyState} direction="column" wrap={false} alignItems="center" gap="xsmall">
    <Heading type="title" size="small">
      {title}
    </Heading>
    <Paragraph>{description}</Paragraph>
  </Stack>
);

export const LoadingState: React.FC<{ label: string }> = ({ label }) => (
  <Stack direction="column" wrap={false} alignItems="center" gap="small" padding="xlarge">
    <Spinner />
    <Text prominence="subtle">{label}</Text>
  </Stack>
);

export const DefinitionList: React.FC<{ items: { label: string; value: React.ReactNode }[] }> = ({ items }) => (
  <dl className={styles.definitionList}>
    {items.map(({ label, value }) => (
      <React.Fragment key={label}>
        <dt>
          <Text size="small" prominence="subtle">
            {label}
          </Text>
        </dt>
        <dd>
          <Text size="small">{value}</Text>
        </dd>
      </React.Fragment>
    ))}
  </dl>
);
